import { useCallback, useRef } from 'react';
import { Logger } from '../utils/Logger';
import { trackEvent } from '../services/AppInsightsPlugin';

/**
 * Hook pour déclencher la mise à jour du Service Worker quand une nouvelle version est détectée
 *
 * Approche robuste:
 * 1. Deux détections de nouvelles versions : needRefresh (Workbox) + SW_VERSION_CHANGED (header API)
 * 2. onSwVersionChanged() affiche un overlay bloquant
 * 3. Attache un listener pour SW_ACTIVATED AVANT d'envoyer SKIP_WAITING
 * 4. updateServiceWorker() envoie SKIP_WAITING au SW EN ATTENTE (via workbox.messageSkipWaiting)
 * 5. Nouveau SW reçoit SKIP_WAITING → skipWaiting() → envoie SW_ACTIVATED aux clients
 * 6. Frontend recharge seulement après SW_ACTIVATED (garantit que le nouveau SW est prêt)
 * 7. Timeout 15s de fallback si SW_ACTIVATED ne revient jamais (crash, message perdu)
 *
 * @param updateServiceWorker Fonction callback pour déclencher la mise à jour du SW
 * @param onUpdateStarted Callback pour afficher l'overlay de chargement
 * @returns Objet avec la fonction onSwVersionChanged
 */
export const useSWVersionCheck = (
  updateServiceWorker: (reloadPage: boolean) => void,
  onUpdateStarted: () => void
) => {
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const activationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onSwVersionChanged = useCallback(() => {
    Logger.debug('useSWVersionCheck', '🔄 Service Worker version changed detected');
    trackEvent('SW_VERSION_CHANGE_HANDLING_STARTED', {
      timestamp: new Date().toISOString()
    });

    // 1. Afficher overlay immédiatement (bloque l'UI)
    onUpdateStarted();

    if (navigator.serviceWorker) {
      // Nettoyer l'ancien listener et timeout s'ils existent
      if (messageHandlerRef.current) {
        navigator.serviceWorker.removeEventListener('message', messageHandlerRef.current);
      }
      if (activationTimeoutRef.current) {
        clearTimeout(activationTimeoutRef.current);
      }

      // 2. Écouter le message SW_ACTIVATED AVANT de déclencher SKIP_WAITING
      // (important: éviter race condition où SW_ACTIVATED arrive avant que le listener soit attaché)
      const handler = (event: MessageEvent) => {
        if (event.data && event.data.type === 'SW_ACTIVATED') {
          Logger.debug('useSWVersionCheck', '✅ SW_ACTIVATED received - reloading with new SW');
          if (activationTimeoutRef.current) {
            clearTimeout(activationTimeoutRef.current);
            activationTimeoutRef.current = null;
          }
          if (messageHandlerRef.current && navigator.serviceWorker) {
            navigator.serviceWorker.removeEventListener('message', messageHandlerRef.current);
          }
          window.location.reload();
        }
      };
      messageHandlerRef.current = handler;
      navigator.serviceWorker.addEventListener('message', handler);

      // Timeout de sécurité : si SW_ACTIVATED n'arrive jamais (crash SW, message perdu)
      activationTimeoutRef.current = setTimeout(() => {
        Logger.warn('useSWVersionCheck', '⚠️ SW_ACTIVATED timeout (5s) - forcing reload');
        if (messageHandlerRef.current && navigator.serviceWorker) {
          navigator.serviceWorker.removeEventListener('message', messageHandlerRef.current);
          messageHandlerRef.current = null;
        }
        window.location.reload();
      }, 5000);

      // 3. Déclencher SKIP_WAITING au SW en attente (nouvelle version)
      // Envoyer directement à registration.waiting car updateServiceWorker() ne fonctionne pas fiablement
      Logger.debug('useSWVersionCheck', '⏱️ Sending SKIP_WAITING to waiting SW...');
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          Logger.debug('useSWVersionCheck', '📤 Posting SKIP_WAITING to registration.waiting');
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        } else {
          // Pas de SW en attente : forcer le navigateur à vérifier les mises à jour
          // sans recharger (le navigateur téléchargera le nouveau sw.js et viendra
          // mettre registration.waiting, ce qui déclenchera needRefresh=true)
          Logger.warn('useSWVersionCheck', '⚠️ No waiting SW — triggering SW update check');
          trackEvent('SW_UPDATE_CHECK_TRIGGERED', {
            scenario: 'no_waiting_sw'
          });
          registration?.update()
            .then(() => {
              Logger.debug('useSWVersionCheck', '✅ SW update check completed successfully');
              trackEvent('SW_UPDATE_CHECK_SUCCESS', {
                scenario: 'no_waiting_sw'
              });
            })
            .catch(() => {
              Logger.warn('useSWVersionCheck', '⚠️ SW update check failed, reloading');
              trackEvent('SW_UPDATE_CHECK_FAILED', {
                scenario: 'no_waiting_sw'
              });
              window.location.reload();
            });
        }
      }).catch((error) => {
        Logger.error('useSWVersionCheck', '❌ Error getting registration:', error);
        window.location.reload();
      });
    } else {
      // Fallback si pas de SW support
      Logger.debug('useSWVersionCheck', '⚠️ No Service Worker support, using fallback');
      setTimeout(() => window.location.reload(), 500);
    }
  }, [updateServiceWorker, onUpdateStarted]);

  return { onSwVersionChanged };
};
